// DispatchDeck — Internal-time hour buckets for payroll (TASK-9387)
//
// PURE module: no Firebase, no DOM, no AppState. Loaded in the browser via ONE
// side-effect import from clock.js (`import '../internalTime.js?v=…'`) which
// sets window.InternalTime — every other module reads window.InternalTime at
// call time (Pitfall #1: never a second `?v=` import). Node unit tests
// require() it directly (UMD): `node tests/task9387-internal-time.unit.cjs`.
//
// WHY THIS EXISTS. A tenant (TITS) pays shop time at a different rate than
// driving time. DD does not price it — the payroll system (Gusto / QBO) holds
// the second rate — but the payroll exports have to SAY how many of each
// person's hours were internal time, per internal customer, per workweek
// (OT is per workweek, so a period total alone can't be keyed correctly).
//
// WHAT COUNTS AS INTERNAL. A CRM customer with `internalTimeOnly === true`, or
// — only when that flag was never set — a customer NAMED exactly
// shop/office/internal/yard. Same rule as _tcxInternalCustomerIds (clock.js)
// and scripts/billable-hours-report.cjs, so the screens never disagree.
//
// 🔴 LEGACY SHOP TIME HAS NO CUSTOMER ID. Before TASK-1175 made Shop a real
// customer, "Shop" was a hardcoded pseudo-customer. The clock-in, switch-job,
// dispatch and jobboard write paths stored it as `customerId: null,
// customerName: 'Shop'`; only the jobboard repoint persisted the literal
// `'__shop__'`. Both forms bucket as Shop here. A BLANK customer is NOT Shop
// (switch-job writes '' / '' for "no customer"); only the literal name is.
(function (root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && typeof root === 'object') root.InternalTime = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const NAME_FALLBACK = /^(shop|office|internal|yard)$/i;
  const LEGACY_SHOP_ID = '__shop__';

  function _name(c) {
    return String((c && (c.name || c.customerName)) || '').trim();
  }

  // Is this customer doc internal time? Flag wins; the name fallback applies
  // ONLY when the flag was never written (undefined) — an explicit `false`
  // means an admin said "bill this", whatever it is called.
  function isInternalCustomer(c) {
    if (!c) return false;
    if (c.internalTimeOnly === true) return true;
    return c.internalTimeOnly === undefined && NAME_FALLBACK.test(_name(c));
  }

  // Resolve the tenant's customer list into the lookup the bucketer needs.
  //   internal: Map id -> label   (internal customers only)
  //   allIds:   Set of every customer id that still exists
  //   shopKey:  the bucket key legacy Shop time merges into — an internal
  //             customer literally named "Shop" when one exists, else the
  //             legacy sentinel so the hours still get their own column.
  //   source:   Map id -> 'flag' | 'name' — HOW the customer was classified.
  //             TASK-9388 applies pay rates only to 'flag' buckets (explicit
  //             internalTimeOnly === true), never to a name guess.
  function buildIndex(customers) {
    const internal = new Map();
    const source = new Map();
    const allIds = new Set();
    (customers || []).forEach(c => {
      if (!c || !c.id) return;
      allIds.add(c.id);
      if (isInternalCustomer(c)) {
        internal.set(c.id, _name(c) || c.id);
        source.set(c.id, c.internalTimeOnly === true ? 'flag' : 'name');
      }
    });
    let shopKey = LEGACY_SHOP_ID;
    const shops = [...internal.entries()]
      .filter(([, label]) => label.toLowerCase() === 'shop')
      .map(([id]) => id)
      .sort();
    if (shops.length) shopKey = shops[0];
    return { internal, source, allIds, shopKey };
  }

  // Which internal bucket does one clockEntry belong to? null = not internal.
  function bucketFor(entry, index) {
    if (!entry || !index) return null;
    const cid = entry.customerId;
    const snap = String(entry.customerName || '').trim();
    const src = (k, fallback) => (index.source && index.source.get(k)) || fallback;
    const shop = () => ({ key: index.shopKey, label: index.internal.get(index.shopKey) || 'Shop', source: src(index.shopKey, 'legacy') });
    if (cid === LEGACY_SHOP_ID) return shop();
    if (!cid) return snap.toLowerCase() === 'shop' ? shop() : null;
    if (index.internal.has(cid)) return { key: cid, label: index.internal.get(cid), source: src(cid, 'name') };
    // Customer HARD-deleted since (crm.js deleteDoc): we can no longer read its
    // flag, so fall back to the entry's name snapshot under the same rule.
    if (!index.allIds.has(cid) && NAME_FALLBACK.test(snap)) {
      return snap.toLowerCase() === 'shop' ? shop() : { key: cid, label: snap, source: 'deleted' };
    }
    return null;
  }

  // A bucket is CONFIRMED internal time only when the tenant ticked
  // "Internal time only" on the customer (source 'flag'). 'name' (regex guess,
  // flag never set — scripts/billable-hours-report.cjs notes it misclassifies),
  // 'legacy' (old Shop pseudo-customer with no flagged Shop customer to merge
  // into) and 'deleted' (snapshot name of a hard-deleted customer) are shown
  // as UNCONFIRMED and never offered as hours to move to another pay rate.
  function isConfirmedSource(source) {
    return source === 'flag';
  }

  function _timeOf(e) {
    const c = e && e.clockIn;
    if (!c) return NaN;
    if (typeof c.toDate === 'function') return c.toDate().getTime();
    if (typeof c.toMillis === 'function') return c.toMillis();
    return new Date(c).getTime();
  }

  // Aggregate internal hours.
  //   entries   — clockEntry docs. Pass the WEEK-EXPANDED set (the one
  //               computeCrossPeriodOT scores paid REG/OT from), INCLUDING
  //               billable entries: they count toward the week's OT threshold.
  //   customers — the tenant's customer docs
  //   opts.weekKeyOf(entry) — the SAME workweek key the caller's OT uses
  //   opts.hoursOf(entry)   — hours to count (default: completed entries'
  //                           totalHours — computeCrossPeriodOT's rule)
  //   opts.position(entry)  — 'in' (default) | 'prior' (clocked in before the
  //                           period) | 'skip' (after the period — never
  //                           affects this period's pay)
  //   opts.weekEndsInPeriod(entry) — does this entry's workweek END inside the
  //                           period? (computeCrossPeriodOT's endsInPeriod —
  //                           OT is paid in the period the week ends in)
  //   opts.otThreshold      — weekly OT threshold (default: none → no OT split)
  //   opts.isExempt(pid)    — no OT for this person (all in-period hours are
  //                           regular, exactly like computeCrossPeriodOT)
  //   opts.timeOf(entry)    — clock-in ms for chronological order
  //
  // HOURS WORKED vs HOURS PAYABLE THIS PERIOD. Each workweek is walked in
  // clock-in order; an entry's hours up to the threshold are REGULAR and are
  // paid in the period the entry falls in, hours past it are OVERTIME and are
  // paid in the period the WEEK ENDS in. Summed over every customer this is
  // exactly computeCrossPeriodOT's periodReg + periodOT, so:
  //   total / byWeek        — raw hours WORKED in the period (contract kept)
  //   payable / payableByWeek — hours PAID this period (may be < worked when
  //                           the week ends next period; may include last
  //                           period's OT hours when the week ends here)
  //   priorByWeek           — raw hours worked LAST period in a week that
  //                           ends here (contract kept; context only)
  //   priorPaidByWeek       — the part of those already PAID last period (as
  //                           regular). 🔴 Never move these: it pays twice.
  // Returns { columns: [{key,label,header,payableHeader,source,confirmed}],
  //   byPerson: { pid: { total, byWeek, priorByWeek, payable, payableByWeek,
  //   priorPaidByWeek } } }. source = 'flag' | 'name' | 'legacy' | 'deleted'
  //   (TASK-9388 seam). Columns are only buckets with hours worked or payable,
  //   sorted by label, duplicate labels disambiguated.
  function aggregate(entries, customers, opts) {
    const o = opts || {};
    const weekKeyOf = typeof o.weekKeyOf === 'function' ? o.weekKeyOf : () => '';
    const hoursOf = typeof o.hoursOf === 'function'
      ? o.hoursOf
      : (e) => (e && e.status === 'completed' ? (Number(e.totalHours) || 0) : 0);
    const positionOf = typeof o.position === 'function' ? o.position : () => 'in';
    const endsHere = typeof o.weekEndsInPeriod === 'function' ? o.weekEndsInPeriod : () => true;
    const threshold = Number.isFinite(o.otThreshold) ? o.otThreshold : Infinity;
    const isExempt = typeof o.isExempt === 'function' ? o.isExempt : () => false;
    const timeOf = typeof o.timeOf === 'function' ? o.timeOf : _timeOf;
    // TASK-9388 — per-customer "keep their normal rate" list (Pay Settings).
    // An excluded person's hours on that customer are NOT broken out: they
    // stay in REG/OT like any other hour and get no internal column (the
    // payroll system has no shop rate for them, e.g. Hayden). Flag buckets
    // only. They still COUNT toward the week's OT threshold (the walk below).
    const cfg = o.payConfig || null;
    // Clock-out ms, for splitting an entry at a rate/exclusion change.
    const outOf = typeof o.outOf === 'function' ? o.outOf : (e) => _ms(e && e.clockOut);
    const index = buildIndex(customers);
    const labels = new Map();
    const sources = new Map();
    const byPerson = {};
    const add = (bag, wk, key, h) => {
      const w = bag[wk] || (bag[wk] = {});
      w[key] = (w[key] || 0) + h;
    };

    // Group every countable entry (internal or not) by person + workweek.
    const weeks = new Map();
    (entries || []).forEach((e, i) => {
      const pid = e && e.personId;
      if (!pid) return;
      const pos = positionOf(e);
      if (pos !== 'in' && pos !== 'prior') return;
      const h = hoursOf(e);
      if (!(h > 0)) return;
      const wk = weekKeyOf(e);
      const gk = pid + '\u0000' + wk;
      let g = weeks.get(gk);
      if (!g) { g = { pid, wk, ends: false, rows: [] }; weeks.set(gk, g); }
      if (endsHere(e)) g.ends = true;
      const t = timeOf(e);
      g.rows.push({ e, i, h, pos, t: Number.isFinite(t) ? t : 0 });
    });

    weeks.forEach(g => {
      const exempt = !!isExempt(g.pid);
      g.rows.sort((a, b) => (a.t - b.t) || (a.i - b.i));
      let cum = 0;
      g.rows.forEach(r => {
        const b = bucketFor(r.e, index);
        // TASK-9388 COD — with Pay Settings config on an explicit-flag
        // customer, an entry is split at every rate / exclusion change and
        // only the pieces where eligibleAt() holds are internal time. The
        // other pieces stay in REG/OT like any hour (and still fill the 40).
        const cc = cfg && b && b.source === 'flag' ? cfg.customers[b.key] : null;
        const pieces = cc ? splitAt(r.t, outOf(r.e), r.h, cutTimes(cc)) : [{ h: r.h, t: r.t }];
        pieces.forEach(pc => {
          const reg = exempt ? pc.h : Math.max(0, Math.min(pc.h, threshold - cum));
          const ot = pc.h - reg;
          cum += pc.h;
          if (!b) return;
          if (cc && !eligibleAt(cfg, b.key, g.pid, pc.t)) return;
          if (r.pos === 'prior' && (!g.ends || exempt)) return; // nothing of it is paid or shown here
          const paidHere = (r.pos === 'in' ? reg : 0) + (g.ends ? ot : 0);
          if (!labels.has(b.key)) { labels.set(b.key, b.label); sources.set(b.key, b.source || 'flag'); }
          const p = byPerson[g.pid] || (byPerson[g.pid] = {
            total: {}, byWeek: {}, priorByWeek: {}, payable: {}, payableByWeek: {}, priorPaidByWeek: {},
          });
          if (r.pos === 'in') {
            p.total[b.key] = (p.total[b.key] || 0) + pc.h;
            add(p.byWeek, g.wk, b.key, pc.h);
          } else {
            add(p.priorByWeek, g.wk, b.key, pc.h);
            if (reg > 0) add(p.priorPaidByWeek, g.wk, b.key, reg);
          }
          if (paidHere > 0) {
            p.payable[b.key] = (p.payable[b.key] || 0) + paidHere;
            add(p.payableByWeek, g.wk, b.key, paidHere);
          }
        });
      });
    });

    const columns = [...labels.entries()]
      .map(([key, label]) => {
        const source = sources.get(key);
        return { key, label, source, confirmed: isConfirmedSource(source) };
      })
      .sort((a, b) => a.label.localeCompare(b.label) || a.key.localeCompare(b.key));
    const seen = new Map();
    columns.forEach(c => {
      const k = c.label.toLowerCase();
      const n = (seen.get(k) || 0) + 1;
      seen.set(k, n);
      if (n > 1) c.label = `${c.label} (${n})`;
      const tag = c.confirmed ? '' : ' (UNCONFIRMED)';
      c.header = `${c.label.toUpperCase()} HRS WORKED${tag}`;
      c.payableHeader = `${c.label.toUpperCase()} HRS PAYABLE THIS PERIOD${tag}`;
    });
    return { columns, byPerson };
  }

  // 'YYYY-MM-DD' -> 'M/D'
  function weekLabel(key) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
    return m ? `${Number(m[2])}/${Number(m[3])}` : String(key || '');
  }

  // Format one { weekKey: { key: h } } map: "Wk 9/1: Shop 8.00; Yard 2.00 ·
  // Wk 9/8: Shop 4.00". Only the given columns. Empty string when none.
  function formatWeeks(weekMap, columns) {
    const m = weekMap || {};
    return Object.keys(m).sort().map(wk => {
      const w = m[wk] || {};
      const parts = (columns || [])
        .filter(c => (w[c.key] || 0) > 0.004)
        .map(c => `${c.label} ${w[c.key].toFixed(2)}`);
      return parts.length ? `Wk ${weekLabel(wk)}: ${parts.join('; ')}` : '';
    }).filter(Boolean).join(' · ');
  }

  // ── TASK-9388 — internal-time pay config ──────────────────────────────────
  // Shape of tenants/{tid}/payConfig/internalTime (written ONLY by the
  // setInternalTimePayConfig callable, which audits old → new):
  //   { otMethod: 'weighted_average' | 'rate_of_work',
  //     customers: { [customerId]: { name, rateHistory: [{rate, effectiveFrom,
  //       setAt, setBy}] (append-only), excludePersonIds: [personId] } } }
  // Missing doc / missing otMethod = weighted average, no rates, no exclusions.
  const OT_METHODS = ['weighted_average', 'rate_of_work'];

  function normalizePayConfig(raw) {
    const r = raw || {};
    const customers = {};
    const src = r.customers && typeof r.customers === 'object' ? r.customers : {};
    Object.keys(src).forEach(id => {
      const c = src[id] || {};
      const exclude = new Set(Array.isArray(c.excludePersonIds) ? c.excludePersonIds.filter(x => typeof x === 'string') : []);
      const rateHistory = Array.isArray(c.rateHistory) ? c.rateHistory : [];
      // TASK-9388 COD — exclusions are effective-dated: [{ids:Set, effMs}].
      // A doc written before excludeHistory existed carries only the current
      // list; it is treated as in effect from the start (the only reading
      // available — it never back-dates anything new).
      let excludeHist = (Array.isArray(c.excludeHistory) ? c.excludeHistory : [])
        .map(e => ({ effMs: _ms(e && e.effectiveFrom), ids: new Set(Array.isArray(e && e.personIds) ? e.personIds.filter(x => typeof x === 'string') : []) }))
        .filter(e => e.effMs !== null)
        .sort((a, b) => a.effMs - b.effMs);
      if (!excludeHist.length && exclude.size) excludeHist = [{ effMs: -Infinity, ids: exclude }];
      const rateHist = rateHistory
        .map(e => ({ effMs: _ms(e && e.effectiveFrom), setAtMs: _ms(e && e.setAt) || 0, rate: Number(e && e.rate) }))
        .filter(e => e.effMs !== null)
        .map(e => ({ ...e, rate: Number.isFinite(e.rate) && e.rate > 0 ? e.rate : null }))
        .sort((a, b) => (a.effMs - b.effMs) || (a.setAtMs - b.setAtMs));
      customers[id] = { name: typeof c.name === 'string' ? c.name : '', rateHistory, exclude, excludeHist, rateHist };
    });
    // TASK-9388 COD (Domain) — OT method history, forward-only. A doc written
    // before otMethodHistory existed: its rate_of_work counts from
    // otMethodSetAt only (never back to the start); no timestamp → weighted.
    let otMethodHistory = (Array.isArray(r.otMethodHistory) ? r.otMethodHistory : [])
      .map(e => ({ method: OT_METHODS.includes(e && e.method) ? e.method : 'weighted_average', effectiveFrom: _ms(e && e.effectiveFrom) }))
      .filter(e => e.effectiveFrom !== null)
      .sort((a, b) => a.effectiveFrom - b.effectiveFrom);
    if (!otMethodHistory.length && r.otMethod === 'rate_of_work' && _ms(r.otMethodSetAt) !== null) {
      otMethodHistory = [{ method: 'rate_of_work', effectiveFrom: _ms(r.otMethodSetAt) }];
    }
    return {
      otMethod: OT_METHODS.includes(r.otMethod) ? r.otMethod : 'weighted_average',
      otMethodHistory,
      customers,
    };
  }

  function _ms(v) {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v.getTime();
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.toDate === 'function') return v.toDate().getTime();
    if (typeof v.seconds === 'number') return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : null;
  }

  // Is this person on the customer's keep-normal-rate list AT time t?
  function excludedAt(c, personId, t) {
    if (!c || !c.excludeHist) return false;
    let ids = null;
    for (const e of c.excludeHist) { if (e.effMs <= t) ids = e.ids; else break; }
    return !!(ids && ids.has(personId));
  }

  // The customer's DD rate AT time t (null before the first save — never
  // back-dated — or after a removal).
  function rateAt(c, t) {
    if (!c || !c.rateHist) return null;
    let r = null;
    for (const e of c.rateHist) { if (e.effMs <= t) r = e.rate; else break; }
    return r;
  }

  // Current exclusion (settings UI + legacy callers).
  function isExcluded(cfg, key, personId) {
    const c = cfg && cfg.customers && cfg.customers[key];
    return !!(c && c.exclude && c.exclude.has(personId));
  }

  // Every moment the pay treatment of this customer can change.
  function cutTimes(c) {
    if (!c) return [];
    return [...(c.rateHist || []).map(e => e.effMs), ...(c.excludeHist || []).map(e => e.effMs)]
      .filter(t => Number.isFinite(t));
  }

  // Is a piece of this person's time on this (explicit-flag) customer at time
  // t internal time to break out / move? No config row → yes (9387: the
  // payroll system holds the rate). Excluded at t → no. The customer has a DD
  // rate history → only while a DD rate is in effect (hours worked before the
  // rate existed stay at the person's rate in EVERY figure).
  function eligibleAt(cfg, key, personId, t) {
    const c = cfg && cfg.customers && cfg.customers[key];
    if (!c) return true;
    if (excludedAt(c, personId, t)) return false;
    if (c.rateHist && c.rateHist.length) return rateAt(c, t) !== null;
    return true;
  }

  // The per-PERSON rate history the pay engine prices with: the customer's
  // rate at each cut, null wherever the person is excluded. Before the first
  // cut there is no entry (→ the person's own rate). [{rate, effectiveFrom}]
  function personRateHistory(c, personId) {
    const cuts = [...new Set(cutTimes(c))].sort((a, b) => a - b);
    return cuts.map(t => ({ rate: excludedAt(c, personId, t) ? null : rateAt(c, t), effectiveFrom: t }));
  }

  // Split one clock entry into pieces at the given cut times, hours
  // proportional to wall time (same rule as payRates.segmentEntry).
  function splitAt(inMs, outMs, hours, cuts) {
    if (!Number.isFinite(inMs) || !Number.isFinite(outMs) || !(outMs > inMs)) return [{ h: hours, t: inMs }];
    const inner = (cuts || []).filter(x => x > inMs && x < outMs).sort((a, b) => a - b);
    if (!inner.length) return [{ h: hours, t: inMs }];
    const b = [inMs, ...inner, outMs];
    const out = [];
    for (let i = 0; i < b.length - 1; i++) {
      if (b[i + 1] > b[i]) out.push({ h: hours * ((b[i + 1] - b[i]) / (outMs - inMs)), t: b[i] });
    }
    return out;
  }

  // The rate on file RIGHT NOW (latest history entry), for the settings UI.
  // null = no rate (pay the person's own rate).
  function currentRate(cfgCustomer) {
    const h = (cfgCustomer && cfgCustomer.rateHistory) || [];
    if (!h.length) return null;
    const ms = (v) => (v && typeof v.toMillis === 'function') ? v.toMillis()
      : (v && typeof v.seconds === 'number') ? v.seconds * 1000 : Number(v) || 0;
    const last = h.slice().sort((a, b) => (ms(a.effectiveFrom) - ms(b.effectiveFrom)) || (ms(a.setAt) - ms(b.setAt)))[h.length - 1];
    const n = Number(last && last.rate);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  // Which internal RATE bucket does this entry pay into for this person?
  // Only an EXPLICIT internalTimeOnly === true customer ('flag'), never a
  // name guess / legacy-only / deleted bucket; the person must not be on that
  // customer's exclude list; and the customer must have a rate history.
  // Returns the key (customer id) or null. Salaried people are the CALLER's
  // call (payrollExemptKind) — this helper doesn't read person docs.
  function rateKeyFor(entry, index, cfg) {
    if (!entry || !index || !cfg) return null;
    const b = bucketFor(entry, index);
    if (!b || b.source !== 'flag') return null;
    const c = cfg.customers[b.key];
    if (!c || !c.rateHistory.length) return null;
    // Exclusions are resolved per piece by the engine (personRateHistory);
    // here only skip a person who never has a DD rate on this customer.
    if (!personRateHistory(c, entry.personId).some(e => e.rate !== null)) return null;
    return b.key;
  }

  // Config rows that no longer apply: the customer was deleted or is no
  // longer flagged internalTimeOnly === true. Their rate/exclusions are
  // IGNORED by payroll; the settings screen lists them with a Remove button
  // so the drift is visible. Only rows that still carry something.
  function staleConfigRows(cfg, customers) {
    const flagged = new Map();
    (customers || []).forEach(c => { if (c && c.id && c.internalTimeOnly === true) flagged.set(c.id, c); });
    return Object.keys((cfg && cfg.customers) || {})
      .filter(id => !flagged.has(id))
      .map(id => ({ id, name: cfg.customers[id].name || id, rate: currentRate(cfg.customers[id]), excludeCount: cfg.customers[id].exclude.size }))
      .filter(r => r.rate !== null || r.excludeCount > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  // Hours WORKED in the period, per workweek. Never splices in last period's
  // hours (those live in priorByWeek / priorPaidByWeek, shown separately).
  function formatByWeek(personAgg, columns) {
    return personAgg ? formatWeeks(personAgg.byWeek, columns) : '';
  }

  return {
    NAME_FALLBACK,
    LEGACY_SHOP_ID,
    OT_METHODS,
    normalizePayConfig,
    isExcluded,
    currentRate,
    rateKeyFor,
    staleConfigRows,
    excludedAt,
    rateAt,
    eligibleAt,
    personRateHistory,
    cutTimes,
    splitAt,
    isInternalCustomer,
    buildIndex,
    bucketFor,
    isConfirmedSource,
    aggregate,
    weekLabel,
    formatWeeks,
    formatByWeek,
  };
});
